import React, { useEffect } from 'react';
import { useEditor, useValue } from 'tldraw';
import { getArrowTerminalPagePoints } from '../utils/arrowTerminalLabelGeometry';
import {
  ARROW_LABEL_MAX,
  arrowLabelMeta,
  readArrowLabel,
} from '../utils/arrowTerminalLabels';

function TerminalLabelChip({ x, y, text }) {
  if (!text) return null;

  return (
    <div
      className="board-link-terminal-label"
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: 'translate(-50%, -50%)',
        maxWidth: 140,
        padding: '2px 8px',
        borderRadius: 999,
        background: 'rgba(255, 255, 255, 0.96)',
        color: 'rgba(15, 23, 42, 0.92)',
        border: '1px solid rgba(99, 102, 241, 0.45)',
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.14)',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.25,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        pointerEvents: 'none',
      }}
      title={text}
    >
      {text}
    </div>
  );
}

function viewportLabelPoint(editor, arrowId) {
  const terminals = getArrowTerminalPagePoints(editor, arrowId);
  if (!terminals) {
    const bounds = editor.getShapePageBounds(arrowId);
    if (!bounds) return null;
    return editor.pageToViewport({
      x: bounds.x + bounds.w * 0.5,
      y: bounds.y + bounds.h * 0.5,
    });
  }

  if (terminals.midPage) return editor.pageToViewport(terminals.midPage);

  const { startPage, endPage } = terminals;
  const startVp = editor.pageToViewport(startPage);
  const endVp = editor.pageToViewport(endPage);
  return {
    x: (startVp.x + endVp.x) / 2,
    y: (startVp.y + endVp.y) / 2,
  };
}

/**
 * Single relationship label at the midpoint of the connect line.
 * Must be InFrontOfTheCanvas — OnTheCanvas sits under shapes and gets covered.
 */
export default function ArrowTerminalLabels() {
  const editor = useEditor();

  // Strip leftover tldraw mid-path text (shows as a smaller duplicate chip).
  useEffect(() => {
    if (!editor || editor.getIsReadonly?.()) return undefined;

    const updates = [];
    for (const shape of editor.getCurrentPageShapes?.() || []) {
      if (!shape || shape.type !== 'arrow') continue;
      const mid = String(shape.props?.text || '').trim();
      if (!mid) continue;

      updates.push({
        id: shape.id,
        type: 'arrow',
        meta: arrowLabelMeta(shape.meta, readArrowLabel(shape) || mid.slice(0, ARROW_LABEL_MAX)),
        props: {
          ...shape.props,
          arrowheadStart: 'none',
          arrowheadEnd: 'arrow',
          text: '',
        },
      });
    }
    if (!updates.length) return undefined;

    editor.run(() => {
      editor.updateShapes(updates);
    }, { history: 'ignore' });
    return undefined;
  }, [editor]);

  const items = useValue(
    'arrow-terminal-label-items',
    () => {
      editor.getCamera?.();

      const next = [];
      for (const shape of editor.getCurrentPageShapes?.() || []) {
        if (!shape || shape.type !== 'arrow') continue;

        const label = readArrowLabel(shape);
        if (!label) continue;

        editor.getBindingsFromShape?.(shape.id, 'arrow');
        const point = viewportLabelPoint(editor, shape.id);
        if (!point) continue;

        next.push({
          id: shape.id,
          label,
          x: point.x,
          y: point.y,
        });
      }
      return next;
    },
    [editor]
  );

  if (!items.length) return null;

  return (
    <div
      className="board-link-terminal-labels"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 250,
        overflow: 'visible',
      }}
    >
      {items.map(item => (
        <TerminalLabelChip key={item.id} x={item.x} y={item.y} text={item.label} />
      ))}
    </div>
  );
}
